import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { projects, Project } from '../data/projects';
function ProjectCard({
  project,
  index
}: {
  project: Project;
  index: number;
}) {
  const [isHovered, setIsHovered] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.7,
    delay: index * 0.1,
    ease: [0.25, 0.1, 0.25, 1]
  }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} className="group relative overflow-hidden bg-[#252525] cursor-pointer" data-id="element-245">
      <Link to={`/projects/${project.slug}`} className="absolute inset-0 z-10" data-id="element-246" />

      {/* Background Image with zoom */}
      <motion.div animate={{
      scale: isHovered ? 1.08 : 1
    }} transition={{
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1]
    }} className="absolute inset-0" data-id="element-247">
        <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-248" />
      </motion.div>

      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/30 to-transparent opacity-80" data-id="element-249" />
      <motion.div animate={{
      opacity: isHovered ? 0.95 : 0.7
    }} transition={{
      duration: 0.4
    }} className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/20 to-transparent" data-id="element-250" />

      {/* Content */}
      <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between" data-id="element-251">
        {/* Top Row */}
        <div className="flex justify-between items-start" data-id="element-252">
          <span className="text-white text-sm font-semibold tracking-wider" data-id="element-253">
            {project.year}
          </span>
          <span className="text-white text-sm font-semibold" data-id="element-254">
            {project.sqft} SF
          </span>
        </div>

        {/* Bottom Content */}
        <div className="flex flex-col justify-end" data-id="element-255">
          <span className="text-white text-xs uppercase tracking-[0.2em] mb-2 block" data-id="element-256">
            {project.location}
          </span>

          <h3 className="text-xl md:text-2xl font-serif font-bold text-white mb-3 leading-tight" data-id="element-257">
            {project.title}
          </h3>

          {/* Features - visible by default, hidden on hover */}
          <motion.div animate={{
          opacity: isHovered ? 0 : 1,
          height: isHovered ? 0 : 'auto'
        }} transition={{
          duration: 0.25
        }} className="flex flex-wrap gap-2 overflow-hidden" data-id="element-258">
            {project.features?.map(feature => <span key={feature} className="text-[#D4CFC8]/80 text-xs font-medium bg-[#1A1A1A]/50 backdrop-blur-sm px-2.5 py-1 border border-[#A07656]/20 rounded-sm" data-id="element-259">
                {feature}
              </span>)}
          </motion.div>

          {/* Description + details + link - visible on hover */}
          <motion.div animate={{
          opacity: isHovered ? 1 : 0,
          height: isHovered ? 'auto' : 0
        }} transition={{
          duration: 0.25
        }} className="overflow-hidden" data-id="element-260">
            <p className="text-white text-sm max-w-sm leading-relaxed mb-3" data-id="element-261">
              {project.description}
            </p>
            <div className="flex gap-3 mb-3" data-id="element-262">
              <div className="bg-[#1A1A1A]/60 backdrop-blur-sm px-3 py-1.5 border border-[#A07656]/20" data-id="element-263">
                <span className="text-[#F5F3F0]/80 text-xs font-medium" data-id="element-264">
                  {project.type}
                </span>
              </div>
              <div className="bg-[#1A1A1A]/60 backdrop-blur-sm px-3 py-1.5 border border-[#A07656]/20" data-id="element-265">
                <span className="text-[#F5F3F0]/80 text-xs font-medium" data-id="element-266">
                  {project.value}
                </span>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 text-white text-sm uppercase tracking-wider font-semibold" data-id="element-267">
              <span className="relative" data-id="element-268">
                View Project
                <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-[#A07656] transition-all duration-300" data-id="element-269" />
              </span>
              <ArrowUpRight className="w-4 h-4" data-id="element-270" />
            </span>
          </motion.div>
        </div>
      </div>

      {/* Border accent on hover */}
      <motion.div animate={{
      opacity: isHovered ? 1 : 0
    }} transition={{
      duration: 0.3
    }} className="absolute inset-0 border-2 border-[#A07656]/40 pointer-events-none" data-id="element-271" />
    </motion.div>;
}
export function PortfolioGrid() {
  return <section id="portfolio" className="relative py-24 md:py-32 bg-[#1A1A1A] text-[#F5F3F0]" data-id="element-272">
      {/* Subtle texture */}
      <div className="absolute inset-0 opacity-20" data-id="element-273">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#252525_0%,transparent_50%)]" data-id="element-274" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-275">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6" data-id="element-276">
          <div data-id="element-277">
            <motion.span initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-[#A07656] uppercase tracking-[0.3em] text-xs font-semibold mb-4 block" data-id="element-278">
              Portfolio
            </motion.span>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-4" data-id="element-279">
              Our Builds
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="text-[#D4CFC8]/60 max-w-lg font-light text-lg" data-id="element-280">
              Custom homes built with precision and care across the Inland
              Northwest.
            </motion.p>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 auto-rows-[400px] md:auto-rows-[520px] lg:auto-rows-[560px]" data-id="element-281">
          {projects.map((project, index) => <ProjectCard key={project.id} project={project} index={index} data-id="element-282" />)}
        </div>
      </div>
    </section>;
}